import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	Box,
	Button,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	SelectChangeEvent,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableFooter,
	TableHead,
	TableRow,
	TextField,
	Typography
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AddCardIcon from "@mui/icons-material/AddCard";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SearchIcon from "@mui/icons-material/Search";
import HomeIcon from "@mui/icons-material/Home";
import { useAuth } from "../../../context/AuthContext";
import { authorizedUsersService } from "../../../firebase/authorization/authorizedUsersService";
import { deleteCashMovement } from "../../../firebase/cashMovement/deleteCashMovement";
import { saveCashMovement } from "../../../firebase/cashMovement/saveCashMovement";
import { useShopCash } from "../../../hooks/useShopCash";
import {
	CashMovementData,
	CashMovementType,
	ExpenseCategory,
	PaymentSource,
	expenseCategories
} from "../../../models/cashMovement";
import { formatDate } from "../../../utils/calculations";
import { exportShopCashToPdf } from "../../../utils/exportShopCashToPdf";
import { ConfirmDialog } from "../../Shared/ConfirmDialog";

const getDateInputValue = () => new Date().toLocaleDateString("en-CA");

const defaultStartDate = "2026-01-01";

const buildMovementDate = (date: string) => `${date}T12:00:00.000Z`;

const formatCurrency = (value: number) => `Rs. ${value.toFixed(2)}`;

export const ShopCash = () => {
	const navigate = useNavigate();
	const { user, userRole } = useAuth();
	const { cashMovements, summary, loading, fetchShopCashData } =
		useShopCash();
	const isAdmin = userRole === "admin";
	const currentDate = getDateInputValue();
	const [startDate, setStartDate] = useState(defaultStartDate);
	const [endDate, setEndDate] = useState(currentDate);
	const [movementType, setMovementType] =
		useState<CashMovementType>("EXPENSE");
	const [paymentSource, setPaymentSource] = useState<PaymentSource>("CASH");
	const [amount, setAmount] = useState("");
	const [movementDate, setMovementDate] = useState(currentDate);
	const [selectedPartner, setSelectedPartner] = useState("");
	const [partnerOptions, setPartnerOptions] = useState<string[]>([]);
	const [expenseCategory, setExpenseCategory] =
		useState<ExpenseCategory>("Stock Purchase");
	const [vendorName, setVendorName] = useState("");
	const [description, setDescription] = useState("");
	const [saving, setSaving] = useState(false);
	const [deletingMovementId, setDeletingMovementId] = useState<string | null>(
		null
	);
	const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

	useEffect(() => {
		fetchShopCashData(startDate, endDate);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const loadPartners = async () => {
			try {
				const activePartners =
					await authorizedUsersService.getActivePartners();
				const partners = activePartners
					.map((user) => user.displayName?.trim() || user.email)
					.filter(Boolean);
				setPartnerOptions(partners);
			} catch (error) {
				console.error("Failed to load partners:", error);
				setPartnerOptions([]);
			}
		};

		void loadPartners();
	}, []);

	const resetForm = () => {
		setMovementType("EXPENSE");
		setPaymentSource("CASH");
		setAmount("");
		setMovementDate(getDateInputValue());
		setSelectedPartner("");
		setExpenseCategory("Stock Purchase");
		setVendorName("");
		setDescription("");
	};

	const handleSearch = () => {
		if (!startDate || !endDate) {
			alert("Please select both start and end dates.");
			return;
		}
		if (startDate > endDate) {
			alert("Start date cannot be after end date.");
			return;
		}
		fetchShopCashData(startDate, endDate);
	};

	const handleExportPdf = () => {
		exportShopCashToPdf({
			startDate,
			endDate,
			cashMovements: activeMovements,
			summary,
			partnerWithdrawalRows,
			expenseCategoryRows,
			totalCashOut,
			totalUpiOut,
			totalOut
		});
	};

	const handleDeleteClick = (movementId: string) => {
		setDeletingMovementId(movementId);
		setConfirmDeleteOpen(true);
	};

	const handleDeleteConfirm = async () => {
		if (!deletingMovementId) {
			return;
		}
		if (!isAdmin) {
			alert("Only administrators can delete cash movements.");
			return;
		}

		try {
			setSaving(true);
			await deleteCashMovement(deletingMovementId);
			setConfirmDeleteOpen(false);
			setDeletingMovementId(null);
			await fetchShopCashData(startDate, endDate);
		} catch (err) {
			console.error("Failed to delete shop cash movement:", err);
			alert("Failed to delete shop cash movement.");
		} finally {
			setSaving(false);
		}
	};

	const handleSave = async () => {
		const parsedAmount = Number(amount);
		const normalizedVendorName = vendorName.trim();
		if (!movementDate) {
			alert("Please select movement date.");
			return;
		}
		if (!parsedAmount || parsedAmount <= 0) {
			alert("Please enter amount greater than zero.");
			return;
		}
		if (!description.trim()) {
			alert("Please enter description.");
			return;
		}
		if (movementType === "PARTNER_WITHDRAWAL" && !selectedPartner.trim()) {
			alert("Please select a registered partner.");
			return;
		}
		if (movementType === "EXPENSE" && !expenseCategory) {
			alert("Please select expense category.");
			return;
		}
		if (
			movementType === "EXPENSE" &&
			expenseCategory === "Stock Purchase" &&
			!normalizedVendorName
		) {
			alert("Please enter vendor/supplier for stock purchase.");
			return;
		}

		const movement: CashMovementData = {
			type: movementType,
			amount: parsedAmount,
			movementDate: buildMovementDate(movementDate),
			description: description.trim(),
			paymentSource,
			createdAt: new Date().toISOString(),
			userId: user?.uid ?? "unknown",
			username: user?.displayName?.trim() ?? user?.email ?? "Unknown",
			...(movementType === "PARTNER_WITHDRAWAL"
				? { personName: selectedPartner.trim() }
				: {
						expenseCategory,
						...(normalizedVendorName
							? { vendorName: normalizedVendorName }
							: {})
					})
		};

		try {
			setSaving(true);
			await saveCashMovement(movement);
			resetForm();
			await fetchShopCashData(startDate, endDate);
		} catch (err) {
			console.error("Failed to save shop cash movement:", err);
			alert("Failed to save shop cash movement.");
		} finally {
			setSaving(false);
		}
	};

	const activeMovements = cashMovements.filter(
		(movement) => !movement.isVoided
	);
	const totalCashOut = activeMovements
		.filter((movement) => movement.paymentSource === "CASH")
		.reduce((sum, movement) => sum + movement.amount, 0);
	const totalUpiOut = activeMovements
		.filter((movement) => movement.paymentSource === "UPI")
		.reduce((sum, movement) => sum + movement.amount, 0);
	const totalOut = totalCashOut + totalUpiOut;
	const partnerWithdrawalTotals = activeMovements
		.filter((movement) => movement.type === "PARTNER_WITHDRAWAL")
		.reduce<Record<string, number>>((totals, movement) => {
			const partnerName =
				movement.personName?.trim() || "Unknown Partner";
			totals[partnerName] = (totals[partnerName] || 0) + movement.amount;
			return totals;
		}, {});
	const partnerWithdrawalRows = Object.entries(partnerWithdrawalTotals).sort(
		(a, b) => b[1] - a[1]
	);
	const expenseCategoryTotals = activeMovements
		.filter((movement) => movement.type === "EXPENSE")
		.reduce<Record<string, number>>((totals, movement) => {
			const categoryName =
				movement.expenseCategory?.trim() || "Uncategorized";
			totals[categoryName] =
				(totals[categoryName] || 0) + movement.amount;
			return totals;
		}, {});
	const expenseCategoryRows = Object.entries(expenseCategoryTotals).sort(
		(a, b) => b[1] - a[1]
	);

	return (
		<Paper elevation={3} sx={{ p: 3, mt: 3 }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					mb: 2,
					gap: 1
				}}
			>
				<Typography variant="h5">Shop Cashbook</Typography>
				<Button
					variant="contained"
					startIcon={<HomeIcon />}
					onClick={() => navigate("/")}
				>
					Go to Home
				</Button>
			</Box>

			<Paper elevation={3} sx={{ p: 3, mb: 3 }}>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: {
							xs: "1fr",
							md: "repeat(5, minmax(0, 1fr))"
						},
						gap: 2,
						alignItems: "center"
					}}
				>
					<TextField
						label="Start Date"
						type="date"
						value={startDate}
						onChange={(e) => setStartDate(e.target.value)}
						InputLabelProps={{ shrink: true }}
						size="small"
						disabled={!isAdmin}
					/>
					<TextField
						label="End Date"
						type="date"
						value={endDate}
						onChange={(e) => setEndDate(e.target.value)}
						InputLabelProps={{ shrink: true }}
						size="small"
						disabled={!isAdmin}
					/>
					<Button
						variant="contained"
						onClick={handleSearch}
						startIcon={<SearchIcon />}
						disabled={loading}
					>
						Search
					</Button>
					<Button
						variant="outlined"
						onClick={handleExportPdf}
						startIcon={
							<PictureAsPdfIcon sx={{ color: "error.main" }} />
						}
						disabled={loading}
					>
						Export PDF
					</Button>
				</Box>
			</Paper>

			<Paper elevation={3} sx={{ p: 3, mb: 3 }}>
				<Typography variant="h6" gutterBottom>
					Add Money Movement
				</Typography>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: {
							xs: "1fr",
							md: "repeat(3, minmax(0, 1fr))"
						},
						gap: 2
					}}
				>
					<FormControl size="small" fullWidth>
						<InputLabel>Type</InputLabel>
						<Select
							value={movementType}
							label="Type"
							onChange={(event: SelectChangeEvent) =>
								setMovementType(
									event.target.value as CashMovementType
								)
							}
						>
							<MenuItem value="EXPENSE">Expense</MenuItem>
							<MenuItem value="PARTNER_WITHDRAWAL">
								Partner Withdrawal
							</MenuItem>
						</Select>
					</FormControl>
					<FormControl size="small" fullWidth>
						<InputLabel>Payment Source</InputLabel>
						<Select
							value={paymentSource}
							label="Payment Source"
							onChange={(event: SelectChangeEvent) =>
								setPaymentSource(
									event.target.value as PaymentSource
								)
							}
						>
							<MenuItem value="CASH">Cash</MenuItem>
							<MenuItem value="UPI">UPI</MenuItem>
						</Select>
					</FormControl>
					<TextField
						label="Amount"
						type="number"
						value={amount}
						onChange={(e) => setAmount(e.target.value)}
						size="small"
						required
						inputProps={{ min: 0, step: 0.01 }}
					/>
					<TextField
						label="Date"
						type="date"
						value={movementDate}
						onChange={(e) => setMovementDate(e.target.value)}
						InputLabelProps={{ shrink: true }}
						size="small"
						disabled={!isAdmin}
					/>
					{movementType === "PARTNER_WITHDRAWAL" ? (
						<FormControl size="small" fullWidth>
							<InputLabel>Partner</InputLabel>
							<Select
								value={selectedPartner}
								label="Partner"
								onChange={(event: SelectChangeEvent) =>
									setSelectedPartner(event.target.value)
								}
								disabled={partnerOptions.length === 0}
							>
								{partnerOptions.length === 0 ? (
									<MenuItem value="" disabled>
										No registered partners found
									</MenuItem>
								) : (
									partnerOptions.map((partner) => (
										<MenuItem key={partner} value={partner}>
											{partner}
										</MenuItem>
									))
								)}
							</Select>
						</FormControl>
					) : (
						<FormControl size="small" fullWidth>
							<InputLabel>Expense Category</InputLabel>
							<Select
								value={expenseCategory}
								label="Expense Category"
								onChange={(event: SelectChangeEvent) =>
									setExpenseCategory(
										event.target.value as ExpenseCategory
									)
								}
							>
								{expenseCategories.map((category) => (
									<MenuItem key={category} value={category}>
										{category}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					)}
					{movementType === "EXPENSE" && (
						<TextField
							label="Vendor/Supplier"
							value={vendorName}
							onChange={(e) => setVendorName(e.target.value)}
							size="small"
							required={expenseCategory === "Stock Purchase"}
						/>
					)}
					<TextField
						label="Description"
						value={description}
						onChange={(e) => setDescription(e.target.value)}
						size="small"
						multiline
						minRows={1}
						required
					/>
				</Box>
				<Box sx={{ mt: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
					<Button
						variant="contained"
						onClick={handleSave}
						startIcon={<AddCardIcon />}
						disabled={saving}
					>
						Save
					</Button>
					<Button variant="outlined" onClick={resetForm}>
						Reset
					</Button>
				</Box>
			</Paper>

			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: {
						xs: "1fr",
						sm: "repeat(2, minmax(0, 1fr))",
						md: "repeat(4, minmax(0, 1fr))"
					},
					gap: 2,
					mb: 3
				}}
			>
				{[
					["Cash Received", summary.cashReceived], // grossCashReceived - cashRefunded
					// ["Cash Refunded", -summary.cashRefunded],
					[
						"Cash Partner Withdrawals",
						-summary.partnerCashWithdrawals
					],
					["Cash Expenses", -summary.shopCashExpenses],
					["Available Cash", summary.availableCash],
					["UPI Received", summary.upiReceived],
					["UPI Partner Withdrawals", -summary.partnerUpiWithdrawals],
					["UPI Expenses", -summary.shopUpiExpenses],
					["Available UPI", summary.availableUpi]
				].map(([label, value]) => {
					const numericValue = value as number;
					return (
						<Paper
							key={label as string}
							elevation={2}
							sx={{ p: 2 }}
						>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									mb: 1
								}}
							>
								<AccountBalanceWalletIcon color="primary" />
								<Typography variant="body2">{label}</Typography>
							</Box>
							<Typography
								variant="h6"
								color={
									numericValue < 0
										? "error.main"
										: "success.main"
								}
								sx={{ fontWeight: 700 }}
							>
								{formatCurrency(numericValue)}
							</Typography>
						</Paper>
					);
				})}
			</Box>

			{(partnerWithdrawalRows.length > 0 ||
				expenseCategoryRows.length > 0) && (
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: {
							xs: "1fr",
							md: "repeat(2, minmax(0, 1fr))"
						},
						gap: 2,
						mb: 3
					}}
				>
					{partnerWithdrawalRows.length > 0 && (
						<Paper elevation={2} sx={{ p: 2 }}>
							<Typography
								variant="subtitle1"
								gutterBottom
								sx={{ fontWeight: 600 }}
							>
								Partner Withdrawal Totals
							</Typography>
							<TableContainer component="div">
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>Partner</TableCell>
											<TableCell align="right">
												Total
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{partnerWithdrawalRows.map(
											([partnerName, total]) => (
												<TableRow key={partnerName}>
													<TableCell>
														{partnerName}
													</TableCell>
													<TableCell
														align="right"
														sx={{
															color: "error.main",
															fontWeight: 600
														}}
													>
														{formatCurrency(total)}
													</TableCell>
												</TableRow>
											)
										)}
									</TableBody>
								</Table>
							</TableContainer>
						</Paper>
					)}
					{expenseCategoryRows.length > 0 && (
						<Paper elevation={2} sx={{ p: 2 }}>
							<Typography
								variant="subtitle1"
								gutterBottom
								sx={{ fontWeight: 600 }}
							>
								Expense Totals by Category
							</Typography>
							<TableContainer component="div">
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>Category</TableCell>
											<TableCell align="right">
												Total
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{expenseCategoryRows.map(
											([categoryName, total]) => (
												<TableRow key={categoryName}>
													<TableCell>
														{categoryName}
													</TableCell>
													<TableCell
														align="right"
														sx={{
															color: "error.main",
															fontWeight: 600
														}}
													>
														{formatCurrency(total)}
													</TableCell>
												</TableRow>
											)
										)}
									</TableBody>
								</Table>
							</TableContainer>
						</Paper>
					)}
				</Box>
			)}

			{loading ? (
				<p>Searching Shop Cashbook...</p>
			) : cashMovements.length === 0 ? (
				<p>No Shop Cashbook entries found.</p>
			) : (
				<TableContainer component={Paper}>
					<Table
						sx={{
							"& .MuiTableCell-root": {
								border: "1px solid rgba(224, 224, 224, 1)"
							},
							"& .MuiTableCell-head": {
								fontWeight: "bold"
							}
						}}
					>
						<TableHead>
							<TableRow>
								<TableCell>Date</TableCell>
								<TableCell>Type</TableCell>
								<TableCell>Category/Person</TableCell>
								<TableCell>Vendor</TableCell>
								<TableCell>Description</TableCell>
								<TableCell>Source</TableCell>
								<TableCell>Amount</TableCell>
								<TableCell>Entered By</TableCell>
								<TableCell align="center">Delete</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{activeMovements.map((movement) => (
								<TableRow key={movement.id}>
									<TableCell>
										{formatDate(movement.movementDate)}
									</TableCell>
									<TableCell>
										{movement.type === "EXPENSE"
											? "Expense"
											: "Partner Withdrawal"}
									</TableCell>
									<TableCell>
										{movement.type === "EXPENSE"
											? movement.expenseCategory
											: movement.personName}
									</TableCell>
									<TableCell>
										{movement.vendorName || "-"}
									</TableCell>
									<TableCell>
										{movement.description}
									</TableCell>
									<TableCell>
										{movement.paymentSource}
									</TableCell>
									<TableCell
										sx={{
											color: movement.isVoided
												? "text.secondary"
												: "error.main",
											fontWeight: 600
										}}
									>
										{formatCurrency(-movement.amount)}
									</TableCell>
									<TableCell>{movement.username}</TableCell>
									<TableCell align="center">
										<IconButton
											size="small"
											color="error"
											onClick={() =>
												handleDeleteClick(movement.id)
											}
											disabled={saving || !isAdmin}
											aria-label={`Delete ${movement.description}`}
										>
											<DeleteOutlineIcon fontSize="small" />
										</IconButton>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
						<TableFooter>
							<TableRow>
								<TableCell
									colSpan={6}
									align="right"
									sx={{ fontWeight: "bold" }}
								>
									Total Cash Out
								</TableCell>
								<TableCell
									sx={{
										fontWeight: "bold",
										color: "error.main"
									}}
								>
									{formatCurrency(-totalCashOut)}
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell
									colSpan={6}
									align="right"
									sx={{ fontWeight: "bold" }}
								>
									Total UPI Out
								</TableCell>
								<TableCell
									sx={{
										fontWeight: "bold",
										color: "error.main"
									}}
								>
									{formatCurrency(-totalUpiOut)}
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell
									colSpan={6}
									align="right"
									sx={{ fontWeight: "bold" }}
								>
									Total Out
								</TableCell>
								<TableCell
									sx={{
										fontWeight: "bold",
										color: "error.main"
									}}
								>
									{formatCurrency(-totalOut)}
								</TableCell>
							</TableRow>
						</TableFooter>
					</Table>
				</TableContainer>
			)}
			<ConfirmDialog
				isOpen={confirmDeleteOpen}
				title="Delete money movement"
				content="This record will be permanently deleted from the cashbook."
				onConfirm={handleDeleteConfirm}
				onCancel={() => {
					setConfirmDeleteOpen(false);
					setDeletingMovementId(null);
				}}
				primaryButtonText="Delete"
				primaryButtonColor="error"
			/>
		</Paper>
	);
};
